/**
 * Off-main-thread twin of `processEnhancedScatterData` (frontend/workers/enhanced-scatter-processor.ts).
 *
 * SimpleScatter calls `processEnhancedScatterData` SYNCHRONOUSLY on the main thread for <=5000 points or
 * whenever a selection exists (SimpleScatter.tsx computeFitGroups), so density optimization + weighted
 * regression block the UI on every data/selection change. This module runs that same computation on a
 * shared @nodets pool: each chart's fit is one `pool.run`, so many scatter charts on a page compute
 * concurrently across cores (the old enhancedScatterWorkerManager funnelled them through ONE serialized
 * worker).
 *
 * @nodets ships the task by source, so `enhancedScatterFitTask` is a single self-contained function with
 * every helper nested and the logging calls dropped (they were dev-only). It is a faithful transcription
 * of the worker module, which stays the source of truth + parity oracle;
 * `__tests__/unit/enhancedScatterFitTask.test.ts` asserts identical output on fixtures across colour
 * types, regression methods and the skipOptimization flag.
 */
/* eslint-disable @typescript-eslint/no-explicit-any --
   Faithful transcription of the `any`-typed enhanced-scatter-processor.ts oracle; the scatter points are
   dynamic records and re-typing them here would diverge from the source this must mirror byte-for-byte. */
import { createPool, type Pool } from '../../utils/parallel/poolManager';

// Mirror of the worker's public config/result shapes (kept `any`-ish like the source).
export interface EnhancedScatterConfig {
  xField: string;
  yField: string;
  colorField?: string;
  colorType?: 'DEFAULT' | 'TACK' | 'GRADE' | 'UW/DW' | 'SOURCE' | 'DAY' | 'EVENT' | 'YEAR' | 'CONFIG' | 'CREW';
  maxPoints?: number;
  regressionMethod?: 'None' | 'Linear' | 'Poly 2' | 'Poly 3' | 'Loess 0.3' | 'Loess 0.5';
  tableRange?: { min: number; max: number; step: number };
  skipOptimization?: boolean;
  sourceColorMap?: Record<string, string>;
  dayColorMap?: Record<string, string>;
  categoryColorMap?: Record<string, string>;
}
export interface EnhancedScatterResult {
  groups: Array<{
    groupName: string;
    color: string;
    data: any[];
    density: number;
    regression?: any;
    tableValues?: { x: number; y: number }[];
  }>;
  totalProcessedCount: number;
  totalValidDataCount: number;
  optimizationStats: { originalCount: number; optimizedCount: number; groupsProcessed: number };
}

export interface EnhancedScatterFitPayload {
  data: any[];
  config: EnhancedScatterConfig;
}

/**
 * Self-contained pool task — MUST reference nothing outside its own body (see file header). Faithful
 * transcription of enhanced-scatter-processor.ts with all helpers nested and logging removed.
 */
export function enhancedScatterFitTask(payload: EnhancedScatterFitPayload): EnhancedScatterResult {
  const { data, config } = payload;

  function getTwaFromPoint(point: any): number {
    return point.Twa ?? point.twa ?? point.Twa_deg ?? point.twa_deg ?? point.TWA ?? 0;
  }

  function getDayGroupKeyFromPoint(point: any): string {
    const rawDate = point.date;
    if (rawDate != null && String(rawDate).trim()) {
      const s = String(rawDate).trim();
      if (/^\d{8}$/.test(s)) {
        return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        return s.slice(0, 10);
      }
    }
    const dt = point.Datetime ?? point.datetime ?? point.timestamp;
    if (dt instanceof Date && !isNaN(dt.getTime())) {
      return dt.toISOString().slice(0, 10);
    }
    if (typeof dt === 'string' && dt.length >= 10) {
      const parsed = Date.parse(dt);
      if (!isNaN(parsed)) {
        return new Date(parsed).toISOString().slice(0, 10);
      }
    }
    if (typeof dt === 'number' && Number.isFinite(dt)) {
      return new Date(dt).toISOString().slice(0, 10);
    }
    return 'unknown';
  }

  function groupDataByColorType(rows: any[], colorType: string): { [key: string]: any[] } {
    const groups: { [key: string]: any[] } = {};
    rows.forEach((point) => {
      let groupKey = 'ALL';
      switch (colorType) {
        case 'TACK':
          groupKey = getTwaFromPoint(point) > 0 ? 'STBD' : 'PORT';
          break;
        case 'GRADE': {
          const grade = point.grade ?? point.Grade ?? point.GRADE ?? 0;
          groupKey = grade === 0 ? 'GRADE_0' : `GRADE_${grade}`;
          break;
        }
        case 'UW/DW': {
          const absTwa = Math.abs(getTwaFromPoint(point));
          if (absTwa < 75) {groupKey = 'UPWIND';}
          else if (absTwa >= 75 && absTwa <= 120) {groupKey = 'REACHING';}
          else {groupKey = 'DOWNWIND';}
          break;
        }
        case 'SOURCE':
          groupKey = String(point.source_name ?? point.SOURCE_NAME ?? '').trim() || 'unknown';
          break;
        case 'DAY':
          groupKey = getDayGroupKeyFromPoint(point);
          break;
        case 'EVENT':
          groupKey = String(point.event ?? point.Event ?? point.EVENT ?? '').trim() || 'unknown';
          break;
        case 'YEAR':
          groupKey = String(point.year ?? point.Year ?? point.YEAR ?? '').trim() || 'unknown';
          break;
        case 'CONFIG':
          groupKey = String(point.config ?? point.Config ?? point.CONFIG ?? '').trim() || 'unknown';
          break;
        case 'CREW':
          groupKey = String(point.crew ?? point.Crew ?? point.CREW ?? point.CREW_CODE ?? '').trim() || 'unknown';
          break;
        default:
          groupKey = 'ALL';
      }
      if (!groups[groupKey]) {groups[groupKey] = [];}
      groups[groupKey].push(point);
    });
    return groups;
  }

  function optimizeDensity(rows: any[], maxPoints: number, xScale: any, yScale: any): any[] {
    if (!rows || rows.length === 0) {return [];}
    if (rows.length <= maxPoints) {return rows;}
    const screenPoints = rows.map((point, idx) => ({
      ...point,
      screenX: xScale(point.x),
      screenY: yScale(point.y),
      originalIndex: idx,
    }));
    const clusterRadius = 3;
    const minClusterSize = 2;
    const gridSize = clusterRadius * 2;
    const grid = new Map<string, any[]>();
    screenPoints.forEach((point) => {
      const gridX = Math.floor(point.screenX / gridSize);
      const gridY = Math.floor(point.screenY / gridSize);
      const key = `${gridX},${gridY}`;
      let cell = grid.get(key);
      if (!cell) {
        cell = [];
        grid.set(key, cell);
      }
      cell.push(point);
    });
    const processedPoints: any[] = [];
    const processedIndices = new Set<number>();
    for (const [, cellPoints] of grid) {
      if (cellPoints.length >= minClusterSize) {
        const clusterCenter = {
          x: cellPoints.reduce((sum, p) => sum + p.x, 0) / cellPoints.length,
          y: cellPoints.reduce((sum, p) => sum + p.y, 0) / cellPoints.length,
          density: cellPoints.length,
          clusterSize: cellPoints.length,
          ...cellPoints[0],
        };
        processedPoints.push({
          ...clusterCenter,
          isCluster: true,
          opacity: Math.min(0.9, 0.15 + cellPoints.length * 0.03),
        });
        cellPoints.forEach((point) => processedIndices.add(point.originalIndex));
      } else {
        cellPoints.forEach((point) => {
          if (!processedIndices.has(point.originalIndex)) {
            processedPoints.push({ ...point, isCluster: false, opacity: 0.1 });
            processedIndices.add(point.originalIndex);
          }
        });
      }
    }
    if (processedPoints.length > maxPoints) {
      processedPoints.sort((a, b) => {
        if (a.isCluster && !b.isCluster) {return -1;}
        if (!a.isCluster && b.isCluster) {return 1;}
        return (b.density || 1) - (a.density || 1);
      });
      const clusters = processedPoints.filter((p) => p.isCluster);
      const individuals = processedPoints.filter((p) => !p.isCluster);
      const remainingSlots = maxPoints - clusters.length;
      const sampleStep = Math.ceil(individuals.length / remainingSlots);
      const sampledIndividuals = individuals.filter((_, index) => index % sampleStep === 0);
      return [...clusters, ...sampledIndividuals];
    }
    return processedPoints;
  }

  function calculateWeightedLinearRegression(rows: any[], weights: number[], xDomain: [number, number]): any {
    const n = rows.length;
    const xMean = rows.reduce((sum, d, i) => sum + d.x * weights[i], 0);
    const yMean = rows.reduce((sum, d, i) => sum + d.y * weights[i], 0);
    let numerator = 0;
    let denominator = 0;
    for (let i = 0; i < n; i++) {
      const xDiff = rows[i].x - xMean;
      const yDiff = rows[i].y - yMean;
      const weight = weights[i];
      numerator += weight * xDiff * yDiff;
      denominator += weight * xDiff * xDiff;
    }
    const slope = denominator !== 0 ? numerator / denominator : 0;
    const intercept = yMean - slope * xMean;
    let ssRes = 0;
    let ssTot = 0;
    for (let i = 0; i < n; i++) {
      const predicted = slope * rows[i].x + intercept;
      const residual = rows[i].y - predicted;
      const total = rows[i].y - yMean;
      ssRes += weights[i] * residual * residual;
      ssTot += weights[i] * total * total;
    }
    const r2 = ssTot !== 0 ? 1 - ssRes / ssTot : 0;
    const points = [
      { x: xDomain[0], y: slope * xDomain[0] + intercept },
      { x: xDomain[1], y: slope * xDomain[1] + intercept },
    ];
    return { slope, intercept, r2, points };
  }

  function solveLinearSystem(A: number[][], b: number[]): number[] {
    const n = A.length;
    const augmented = A.map((row, i) => [...row, b[i]]);
    for (let i = 0; i < n; i++) {
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {maxRow = k;}
      }
      [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];
      for (let k = i + 1; k < n; k++) {
        const factor = augmented[k][i] / augmented[i][i];
        for (let j = i; j <= n; j++) {
          augmented[k][j] -= factor * augmented[i][j];
        }
      }
    }
    const x = new Array(n);
    for (let i = n - 1; i >= 0; i--) {
      x[i] = augmented[i][n];
      for (let j = i + 1; j < n; j++) {
        x[i] -= augmented[i][j] * x[j];
      }
      x[i] /= augmented[i][i];
    }
    return x;
  }

  function solvePolynomialSystem(X: number[][], y: number[], n: number): number[] {
    const m = X.length;
    const XTX: number[][] = [];
    for (let i = 0; i < n; i++) {
      XTX[i] = [];
      for (let j = 0; j < n; j++) {
        let sum = 0;
        for (let k = 0; k < m; k++) {
          sum += X[k][i] * X[k][j];
        }
        XTX[i][j] = sum;
      }
    }
    const XTy: number[] = [];
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let j = 0; j < m; j++) {
        sum += X[j][i] * y[j];
      }
      XTy[i] = sum;
    }
    return solveLinearSystem(XTX, XTy);
  }

  function calculateWeightedPolynomialRegression(
    rows: any[],
    weights: number[],
    xDomain: [number, number],
    degree: number
  ): any {
    const n = rows.length;
    const xValues = rows.map((d) => d.x);
    const yValues = rows.map((d) => d.y);
    const X: number[][] = [];
    const y: number[] = [];
    for (let i = 0; i < n; i++) {
      const row: number[] = [];
      for (let j = 0; j <= degree; j++) {
        row.push(Math.pow(xValues[i], j));
      }
      X.push(row);
      y.push(yValues[i] * Math.sqrt(weights[i]));
    }
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= degree; j++) {
        X[i][j] *= Math.sqrt(weights[i]);
      }
    }
    const coefficients = solvePolynomialSystem(X, y, degree + 1);
    const numPoints = 50;
    const points: any[] = [];
    for (let i = 0; i <= numPoints; i++) {
      const x = xDomain[0] + (i / numPoints) * (xDomain[1] - xDomain[0]);
      let yy = 0;
      for (let j = 0; j <= degree; j++) {
        yy += coefficients[j] * Math.pow(x, j);
      }
      points.push({ x, y: yy });
    }
    return { coefficients, points, degree };
  }

  function calculateWeightedLoessRegression(
    rows: any[],
    weights: number[],
    xDomain: [number, number],
    bandwidth = 0.3
  ): any {
    const numPoints = 50;
    const points: any[] = [];
    for (let i = 0; i <= numPoints; i++) {
      const x = xDomain[0] + (i / numPoints) * (xDomain[1] - xDomain[0]);
      let numerator = 0;
      let denominator = 0;
      for (let j = 0; j < rows.length; j++) {
        const distance = Math.abs(rows[j].x - x) / (xDomain[1] - xDomain[0]);
        const weight = weights[j] * Math.max(0, 1 - Math.pow(distance / bandwidth, 3));
        numerator += weight * rows[j].y;
        denominator += weight;
      }
      const yy = denominator > 0 ? numerator / denominator : 0;
      points.push({ x, y: yy });
    }
    return { points, bandwidth };
  }

  function calculateWeightedRegression(rows: any[], method: string, xDomain: [number, number]): any {
    if (!rows || rows.length < 2) {return null;}
    const validData = rows.filter(
      (d) =>
        d.x !== undefined && d.x !== null && d.x !== '' &&
        d.y !== undefined && d.y !== null && d.y !== '' &&
        Number.isFinite(Number(d.x)) && Number.isFinite(Number(d.y))
    );
    if (validData.length < 2) {return null;}
    const weights = validData.map((d) => d.density || 1);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    const normalizedWeights = weights.map((w) => w / totalWeight);
    switch (method) {
      case 'Linear':
        return calculateWeightedLinearRegression(validData, normalizedWeights, xDomain);
      case 'Poly 2':
        return calculateWeightedPolynomialRegression(validData, normalizedWeights, xDomain, 2);
      case 'Poly 3':
        return calculateWeightedPolynomialRegression(validData, normalizedWeights, xDomain, 3);
      case 'Loess 0.3':
        return calculateWeightedLoessRegression(validData, normalizedWeights, xDomain, 0.3);
      case 'Loess 0.5':
        return calculateWeightedLoessRegression(validData, normalizedWeights, xDomain, 0.5);
      default:
        return null;
    }
  }

  function interpolateRegressionValue(points: { x: number; y: number }[], targetX: number): number | null {
    if (!points || points.length === 0) {return null;}
    let lowerPoint: { x: number; y: number } | null = null;
    let upperPoint: { x: number; y: number } | null = null;
    for (const point of points) {
      if (point.x <= targetX && (!lowerPoint || point.x > lowerPoint.x)) {lowerPoint = point;}
      if (point.x >= targetX && (!upperPoint || point.x < upperPoint.x)) {upperPoint = point;}
    }
    if (lowerPoint && lowerPoint.x === targetX) {return lowerPoint.y;}
    if (upperPoint && upperPoint.x === targetX) {return upperPoint.y;}
    if (lowerPoint && upperPoint) {
      const ratio = (targetX - lowerPoint.x) / (upperPoint.x - lowerPoint.x);
      return lowerPoint.y + ratio * (upperPoint.y - lowerPoint.y);
    }
    if (lowerPoint && targetX > lowerPoint.x) {return lowerPoint.y;}
    if (upperPoint && targetX < upperPoint.x) {return upperPoint.y;}
    return null;
  }

  function generateTableValues(
    regression: any,
    tableRange: { min: number; max: number; step: number }
  ): { x: number; y: number }[] {
    if (!regression || !regression.points) {return [];}
    const values: { x: number; y: number }[] = [];
    for (let x = tableRange.min; x <= tableRange.max; x += tableRange.step) {
      const y = interpolateRegressionValue(regression.points, x);
      if (y !== null) {values.push({ x, y });}
    }
    return values;
  }

  // ---- main (mirror of processEnhancedScatterData) ----
  const {
    xField = 'x',
    yField = 'y',
    colorType = 'DEFAULT',
    maxPoints = 3000,
    regressionMethod = 'None',
    tableRange = { min: 6, max: 20, step: 1 },
    skipOptimization = false,
    sourceColorMap,
    dayColorMap,
    categoryColorMap,
  } = config;

  const validData = data.filter((item) => {
    // Reject null/undefined/empty BEFORE coercion: Number(null) and Number('') are 0, so the old
    // post-coercion null check was dead code and let missing x/y through as 0, polluting the fit.
    const xr = item[xField];
    const yr = item[yField];
    if (xr === null || xr === undefined || xr === '' || yr === null || yr === undefined || yr === '') {
      return false;
    }
    const x = Number(xr);
    const y = Number(yr);
    return Number.isFinite(x) && Number.isFinite(y);
  });

  if (validData.length === 0) {
    return {
      groups: [],
      totalProcessedCount: 0,
      totalValidDataCount: 0,
      optimizationStats: { originalCount: data.length, optimizedCount: 0, groupsProcessed: 0 },
    };
  }

  const groupedData = groupDataByColorType(validData, colorType);
  const groups: EnhancedScatterResult['groups'] = [];
  let totalOptimizedCount = 0;

  const xValues = validData.map((d) => d[xField]);
  const yValues = validData.map((d) => d[yField]);
  const xDomain: [number, number] = [Math.min(...xValues), Math.max(...xValues)];
  const yDomain: [number, number] = [Math.min(...yValues), Math.max(...yValues)];
  const xScale = (x: number) => ((x - xDomain[0]) / (xDomain[1] - xDomain[0])) * 800;
  const yScale = (y: number) => ((y - yDomain[0]) / (yDomain[1] - yDomain[0])) * 400;

  for (const [groupName, groupData] of Object.entries(groupedData)) {
    if (groupData.length === 0) {continue;}
    if (groupName === 'SKIP') {continue;}

    const optimizedData = skipOptimization ? groupData : optimizeDensity(groupData, maxPoints, xScale, yScale);

    let regression = null;
    let tableValues: { x: number; y: number }[] = [];
    if (regressionMethod !== 'None' && groupName !== 'GRADE_0') {
      regression = calculateWeightedRegression(optimizedData, regressionMethod, xDomain);
      if (regression) {
        tableValues = generateTableValues(regression, tableRange);
      }
    }

    let color = '#1f77b4';
    if (colorType === 'SOURCE' && sourceColorMap && sourceColorMap[groupName]) {
      color = sourceColorMap[groupName];
    } else if (colorType === 'DAY' && dayColorMap && dayColorMap[groupName]) {
      color = dayColorMap[groupName];
    } else if (
      (colorType === 'EVENT' || colorType === 'YEAR' || colorType === 'CONFIG' || colorType === 'CREW') &&
      categoryColorMap &&
      categoryColorMap[groupName]
    ) {
      color = categoryColorMap[groupName];
    } else {
      switch (groupName) {
        case 'PORT': color = '#d62728'; break;
        case 'STBD': color = '#2ca02c'; break;
        case 'UPWIND': color = 'blue'; break;
        case 'REACHING': color = 'orange'; break;
        case 'DOWNWIND': color = 'purple'; break;
        case 'GRADE_0': color = 'lightgray'; break;
        case 'GRADE_1': color = 'red'; break;
        case 'GRADE_2': color = 'lightgreen'; break;
        case 'GRADE_3': color = 'darkgreen'; break;
      }
    }

    groups.push({ groupName, color, data: optimizedData, density: groupData.length, regression, tableValues });
    totalOptimizedCount += optimizedData.length;
  }

  return {
    groups,
    totalProcessedCount: totalOptimizedCount,
    totalValidDataCount: validData.length,
    optimizationStats: {
      originalCount: data.length,
      optimizedCount: totalOptimizedCount,
      groupsProcessed: groups.length,
    },
  };
}

// Shared, lazily-created pool. Scatter fits are interactive (recompute on selection/zoom), so a
// persistent pool avoids per-interaction worker spawn; many charts' fits run concurrently across cores.
let fitPool: Pool<EnhancedScatterFitPayload, EnhancedScatterResult> | null = null;
function getFitPool(): Pool<EnhancedScatterFitPayload, EnhancedScatterResult> {
  if (!fitPool) {fitPool = createPool<EnhancedScatterFitPayload, EnhancedScatterResult>(enhancedScatterFitTask);}
  return fitPool;
}

/** Compute one chart's enhanced-scatter fit off the main thread (inline fallback when workers absent). */
export function computeEnhancedScatterFit(
  data: any[],
  config: EnhancedScatterConfig
): Promise<EnhancedScatterResult> {
  return getFitPool().run({ data, config });
}
